// web-staking-estimator-frontend/src/hooks/useMemberInfo.js
import { useState, useCallback, useEffect, useRef } from "react";
import { formatUSPhone } from "../utils/formatting";
import {
  requiredMissing,
  missingForStartRequest,
  missingForGenerateMember
} from "../utils/validation";

/**
 * Custom hook to manage member information state and validation
 * @param {object} options - Hook options
 * @param {string} [options.defaultState="OR"] - Default state abbreviation from config
 * @returns {object} Hook utilities and state
 */
export function useMemberInfo({ defaultState = "OR" } = {}) {
  const [member, setMember] = useState({
    first_name: "",
    last_name: "",
    business_name: "",
    street: "",
    city: "",
    state: defaultState,
    zip: "",
    phone: "",
    email: "",
  });

  // Track if we've applied the config-based default yet
  const appliedConfigDefault = useRef(false);

  // When config loads with a non-OR default, apply it once
  useEffect(() => {
    if (defaultState && defaultState !== "OR" && !appliedConfigDefault.current) {
      setMember((m) => ({ ...m, state: defaultState }));
      appliedConfigDefault.current = true;
    }
  }, [defaultState]);

  /**
   * Handle phone number input, extracting only digits
   */
  const handlePhoneChange = useCallback((e) => {
    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
    setMember((m) => ({ ...m, phone: digits }));
  }, []);

  /**
   * Get formatted phone number for display
   */
  const formattedPhone = formatUSPhone(member.phone);

  /**
   * Check if member info is complete for generating estimate
   */
  const memberOk = missingForGenerateMember(member).length === 0;

  /**
   * Get list of missing required fields
   */
  const missingFields = requiredMissing(member);

  /**
   * Get missing fields for starting service request
   * Returns a function so it's called dynamically
   */
  const getMissingForRequest = () => missingForStartRequest(member);

  return {
    member,
    setMember,
    handlePhoneChange,
    formattedPhone,
    memberOk,
    missingFields,
    getMissingForRequest,
  };
}
